import React, {useEffect, useState} from 'react';
import Constants, {LibraryTypes} from '../../constants/Constants';
import config from '../../service/config';
import {MediaLocationLink, MediaLocationSeparator, MediaLocationContainer} from './MediaDetailsLink';
import collectionApi from '../../service/collectionApi';
import {ICollectionSummaryModel, IUserProfileModel} from '../../model/appModel';
import logging from '../../service/logging';
import {StringParam, useQueryParam} from 'use-query-params';
import {useTranslation} from 'react-i18next';
import profileApi from '../../service/profileApi';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {useTscTheme} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';
import Optional from '../../model/optional';
import {IBaseLibrary} from '../../model/library';
import {IMinimalFolder} from '../../model/minimalFolder';
import teamApi from '../../service/teamApi';

export const MediaLocationBase: React.FC<IMediaLocationProps & IStateMappedProps> = ({TeamId, OwnerLibrary, ParentFolder, Author, OriginatorId}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [ltiSessionType] = useQueryParam(Constants.queryParams.ltiSessionType, StringParam);
   const [ltiFolderHash] = useQueryParam(Constants.queryParams.ltiSessionFolderHash, StringParam);
   const [referringGroupName, setReferringGroupName] = useState('');
   const [mediaOwnerName, setMediaOwnerName] = useState('');
   const [teamName, setTeamName] = useState(null);

   useEffect(() => {
      if (!config.watchConfig.referringGroup || config.environmentData.IsLti) {
         return;
      }

      const fetchCollectionName = async () => {
         try {
            const collection: ICollectionSummaryModel = await collectionApi.getCollectionSummaryById(config.watchConfig.referringGroup);
            setReferringGroupName(collection.Name);
         } catch (ex) {
            // 404 or permissions error means collection will never show
            logging.warn(ex);
         }
      };

      void fetchCollectionName();
   }, []);

   useEffect(() => {
      if (TeamId) {
         teamApi.getTeam(TeamId).then(team => {
            team.Name && setTeamName(team.Name);
         }).catch(() => {});
      }
   }, [TeamId]);

   const isCurrentUsersLibrary = OwnerLibrary.LibraryType === LibraryTypes.personal && !!OwnerLibrary.User?.TechSmithId && config.user.TechSmithId === OwnerLibrary.User.TechSmithId;
   const isTeamLibrary = OwnerLibrary.LibraryType === LibraryTypes.team;

   useEffect(() => {
      const fetchMediaOwnerName = async () => {
         const mediaOwnerTechSmithId: string = OwnerLibrary.User ? OwnerLibrary.User.TechSmithId : OriginatorId;

         try {
            const mediaOwner: IUserProfileModel = await profileApi.getUserProfileInfo(mediaOwnerTechSmithId);
            setMediaOwnerName(mediaOwner.displayName);
         } catch (ex) {
            setMediaOwnerName(Author);
            logging.error(`Failed getting user profile for ${mediaOwnerTechSmithId}. ex: `, ex);
         }
      };

      void (!ParentFolder && !isCurrentUsersLibrary && !isTeamLibrary && fetchMediaOwnerName());
   }, [ParentFolder]);

   const mediaCollectionLink = `${Constants.navigation.collectionsViewBasePath}/${config.watchConfig.referringGroup}`;

   let mediaLocationText;
   if (ParentFolder) {
      mediaLocationText = ParentFolder.Name;
   } else if (isTeamLibrary) {
      mediaLocationText = t('LibraryForTeam', {teamName: teamName});
   } else if (isCurrentUsersLibrary) {
      mediaLocationText = t('MyLibrary');
   } else if (mediaOwnerName) {
      mediaLocationText = t('LibraryForUser', {userDisplayName: mediaOwnerName});
   }

   let mediaLocationHref = Constants.navigation.myLibraryLink;
   if (config.isAdmin && !config.watchConfig.isOwner && OwnerLibrary.User) {
      mediaLocationHref = `/Users/${OwnerLibrary.User.TechSmithId}`;
   }

   if (ParentFolder) {
      mediaLocationHref = `/folder/${ParentFolder.Hash}`;
   }

   if (config.environmentData.IsLti && ltiSessionType === 'folder') {
      mediaLocationHref = `/folder?isLtiSession=true&id=${ltiFolderHash}`;
      mediaLocationText = t('general.back');
   }

   if (isTeamLibrary && ParentFolder) {
      mediaLocationHref = `/teams/${TeamId}/folder/${ParentFolder.Hash}`;
   } else if (isTeamLibrary) {
      mediaLocationHref = `/teams/${TeamId}`;
   }

   const showLocation = ((config.watchConfig.isOwnerAdminOrTeamEditor) && !config.environmentData.IsLti) || (config.environmentData.IsLti && ltiSessionType === 'folder');

   return (
      <MediaLocationContainer data-test-id="media-location-container">
         {showLocation && (
            <MediaLocationLink
               href={mediaLocationHref}
               theme={theme}
            >
               {mediaLocationText}
            </MediaLocationLink>
         )}
         {showLocation && referringGroupName && <MediaLocationSeparator />}
         {referringGroupName && (
            <MediaLocationLink
               href={mediaCollectionLink}
               theme={theme}
            >
               {referringGroupName}
            </MediaLocationLink>
         )}
      </MediaLocationContainer>
   );
};

export interface IMediaLocationProps {
   TeamId: string;
}

export interface IStateMappedProps {
   OwnerLibrary: Optional<IBaseLibrary>;
   ParentFolder: Optional<IMinimalFolder>;
   Author: string;
   OriginatorId: string;
}

const mapStatesToProps = (
   {TeamId}: Partial<IMediaLocationProps>,
   {OwnerLibrary, ParentFolder, Author, OriginatorId}: Partial<IStateMappedProps>
): IMediaLocationProps & IStateMappedProps => ({TeamId, OwnerLibrary, ParentFolder, Author, OriginatorId});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(MediaLocationBase);
